import { ThunkAction } from 'redux-thunk';
import { Action, ActionCreator, Reducer } from 'redux';
import CustomError from '../classes/CustomError';
import { getTracingHeaders } from '../util/helpers';
import type { RootState } from './index';

// action types

export const VALIDATE = 'auth/VALIDATE';
export const PROCESSING = 'auth/PROCESSING';
export const ERROR = 'auth/ERROR';

// action creators

export const validate: ActionCreator<ThunkAction<
Promise<Action>,
RootState,
void,
Action
>> = () => async (dispatch, getState): Promise<Action> => {
  try {
    dispatch({ type: PROCESSING });
    const {
      config: {
        apiUri,
        universalAccessToken,
        customerToken,
        registerToken,
        traceid,
        spId,
      },
    } = getState();

    const forgeRockAuth = (spId
      && !universalAccessToken
      && !registerToken
      && !customerToken);

    let auth;
    if (universalAccessToken) {
      auth = `Bearer ${universalAccessToken}`;
    } else if (!forgeRockAuth) {
      auth = `Register ${registerToken}`;
    }

    const headers: Record<string, string> = {
      Accept: 'application/json; charset=utf-8',
    };

    if (customerToken) {
      headers['x-customer-token'] = customerToken;
    }

    let path = '/identity';

    if (!forgeRockAuth) {
      headers.Authorization = auth;
      path = universalAccessToken || customerToken ? '/userprofile' : '/token';
    }

    const response = await fetch(`${apiUri}${path}`, {
      method: 'GET',
      mode: 'cors',
      credentials: 'same-origin',
      headers: { ...headers, ...getTracingHeaders(traceid) },
    });
    const body = (await response.json()) as {
      registerId: string;
      lcId: string;
      spId: string;
      accessToken: string;
      username: string;
    };
    if (!response.ok) {
      const { error: statusText, message, statusCode } = (body as unknown) as {
        error: string;
        message: string;
        statusCode: number;
      };
      throw new CustomError({ statusText, message, statusCode });
    }
    const {
      registerId,
      lcId,
      spId: spId1,
      accessToken,
      username = 'unknown',
    } = body as {
      registerId: string;
      lcId: string;
      spId: string;
      accessToken: string;
      username: string;
    };
    return dispatch({
      type: VALIDATE,
      registerId,
      lcId,
      spId: spId1,
      accessToken,
      username,
    });
  } catch (error) {
    dispatch({ type: ERROR, error });
    throw error;
  }
};

export type AuthState = {
  accessToken?: string;
  spId?: string;
  username?: string;
  lcId?: string;
  registerId?: string;
  processing: boolean;
  error: boolean;
};

const initial: AuthState = {
  processing: false,
  error: false,
};

type AuthAction = Action<typeof VALIDATE | typeof PROCESSING | typeof ERROR> &
AuthState;

// reducer
export const reducer: Reducer<AuthState, AuthAction> = (
  state = initial,
  action = {
    type: undefined,
    ...initial,
  },
) => {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    type,
    registerId,
    lcId,
    spId,
    accessToken,
    username,
    error,
  } = action;
  switch (type) {
    case VALIDATE:
      return {
        registerId,
        lcId,
        spId,
        accessToken,
        username,
        processing: false,
        error: false,
      };
    case PROCESSING:
      return { ...state, processing: true, error: false };
    case ERROR:
      return { ...state, processing: false, error };
    default:
      return state;
  }
};
